// Change #100326 - Settings > WhatsApp > Consent. Opt-in / opt-out per phone number.
// Opted-out numbers cannot be messaged and automatic notifications skip them.
import React, { useEffect, useState } from "react";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import { errorText, ok, searchConsent, setConsent } from "../../../../Components/WhatsApp/whatsappApi";
import { DateCell, ListState, Switch, isOn } from "./shared";

const SOURCES = { STAFF: "Added by staff", WHATSAPP_KEYWORD: "Customer replied STOP / START" };
const sourceLabel = (s) => (s ? SOURCES[String(s).toUpperCase()] || String(s).replace(/_/g, " ") : "");

const ConsentTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [optedOutOnly, setOptedOutOnly] = useState(0);
  const [form, setForm] = useState({ ContactNumber: "", FlagOptedIn: 0, Notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async (number = search, outOnly = optedOutOnly) => {
    setLoading(true);
    const filters = {};
    if (number.trim()) filters.ContactNumber = number.trim();
    if (outOnly) filters.FlagOptedOut = 1;
    const res = await searchConsent(filters);
    setLoading(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load consent records"));
      return;
    }
    setRows(res.messagingcontactconsent_list || []);
  };

  useEffect(() => {
    load("", 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.ContactNumber.trim() || saving) return;
    setSaving(true);
    const res = await setConsent({
      ContactNumber: form.ContactNumber.trim(),
      FlagOptedIn: form.FlagOptedIn,
      Source: "STAFF",
      Notes: form.Notes.trim() || undefined,
    });
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not save the consent record"));
      return;
    }
    toast.success(res.message || "Saved");
    setForm({ ContactNumber: "", FlagOptedIn: 0, Notes: "" });
    load();
  };

  return (
    <>
      <div className="wa-settings__callout mb-3">
        <i className="ri-information-line me-1 text-primary" />
        Customers who reply STOP are opted out automatically; START opts them back in. Opted-out numbers can't be
        messaged and automatic notifications skip them.
      </div>

      <form className="wa-settings__card mb-3" onSubmit={save}>
        <div className="wa-settings__section-title">Add record</div>
        <Row className="g-2 align-items-end">
          <Col md={3}>
            <Label htmlFor="wa-cs-number" className="small">
              Number
            </Label>
            <Input
              id="wa-cs-number"
              bsSize="sm"
              value={form.ContactNumber}
              onChange={(e) => setForm((f) => ({ ...f, ContactNumber: e.target.value }))}
              placeholder="+44 7700 900123"
            />
          </Col>
          <Col md="auto">
            <div className="wa-settings__choice wa-settings__choice--sm" role="radiogroup" aria-label="Consent">
              <button
                type="button"
                role="radio"
                aria-checked={form.FlagOptedIn === 1}
                className={form.FlagOptedIn === 1 ? "is-active" : ""}
                onClick={() => setForm((f) => ({ ...f, FlagOptedIn: 1 }))}
              >
                Opt in
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={form.FlagOptedIn === 0}
                className={form.FlagOptedIn === 0 ? "is-active" : ""}
                onClick={() => setForm((f) => ({ ...f, FlagOptedIn: 0 }))}
              >
                Opt out
              </button>
            </div>
          </Col>
          <Col md>
            <Label htmlFor="wa-cs-notes" className="small">
              Notes
            </Label>
            <Input
              id="wa-cs-notes"
              bsSize="sm"
              value={form.Notes}
              onChange={(e) => setForm((f) => ({ ...f, Notes: e.target.value }))}
              placeholder="e.g. Ticked the box on the job sheet"
            />
          </Col>
          <Col md="auto">
            <Button type="submit" color="primary" size="sm" disabled={!form.ContactNumber.trim() || saving}>
              {saving ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              ) : (
                <i className="ri-save-line me-1" />
              )}
              Save
            </Button>
          </Col>
        </Row>
      </form>

      <form
        className="d-flex flex-wrap align-items-center gap-3 mb-2"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="input-group input-group-sm wa-settings__search">
          <Input
            bsSize="sm"
            aria-label="Search by number"
            placeholder="Search by number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" color="light" disabled={loading}>
            <i className="ri-search-line" />
          </Button>
          {search && (
            <Button
              type="button"
              color="light"
              title="Clear"
              onClick={() => {
                setSearch("");
                load("", optedOutOnly);
              }}
            >
              <i className="ri-close-line" />
            </Button>
          )}
        </div>
        <Switch
          id="wa-cs-out"
          checked={!!optedOutOnly}
          label="Opted out only"
          onChange={(v) => {
            setOptedOutOnly(v);
            load(search, v);
          }}
        />
      </form>

      <ListState
        loading={loading && !rows.length}
        empty={!rows.length}
        icon="ri-shield-user-line"
        title={search || optedOutOnly ? "No matching records" : "No consent records yet"}
        text={search || optedOutOnly ? "Try another number or clear the filter." : "Records appear here when a customer replies STOP or START, or when you add one."}
      >
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>Number</th>
                <th>Status</th>
                <th>Opted in</th>
                <th>Opted out</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // setConsent clears the opt-out on an opt-in but not the reverse, so opted out wins.
                const out = isOn(r.FlagOptedOut);
                const inn = !out && isOn(r.FlagOptedIn);
                return (
                  <tr key={r.Id}>
                    <td className="text-nowrap fw-medium">{r.ContactNumber}</td>
                    <td>
                      {out ? (
                        <span className="badge bg-danger-subtle text-danger">Opted out</span>
                      ) : inn ? (
                        <span className="badge bg-success-subtle text-success">Opted in</span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-secondary">Not recorded</span>
                      )}
                    </td>
                    <td>
                      {r.OptedInDateTime ? (
                        <>
                          <DateCell value={r.OptedInDateTime} />
                          {r.OptInSource && <div className="small text-muted">{sourceLabel(r.OptInSource)}</div>}
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {r.OptedOutDateTime ? (
                        <>
                          <DateCell value={r.OptedOutDateTime} />
                          {r.OptOutSource && <div className="small text-muted">{sourceLabel(r.OptOutSource)}</div>}
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="small">{r.Notes || <span className="text-muted">-</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </ListState>
    </>
  );
};

export default ConsentTab;
